import { supabase } from "@/supabase";

export const namespaced = true;

// Corredors tal com els ha confirmat Supabase
export const state = {
    list: {},
};

// Fila de Supabase -> objecte que fa servir l'app
export function fromRow(row) {
    return {
        id: row.id,
        name: row.name,
        surname: row.surname,
        number: row.number,
        sex: row.sex,
        club: row.club,
        category: row.category,
        laps: row.laps || [],
        version: row.version || 0,
    };
}

// Camps que es poden editar des del formulari / importació
export function toFields(racer) {
    return {
        name: (racer.name || "").trim(),
        surname: (racer.surname || "").trim(),
        number: parseInt(racer.number) || 0,
        sex: racer.sex,
        club: racer.club,
        category: racer.category,
    };
}

function newId() {
    if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }
    return "r" + Date.now() + Math.random().toString(36).slice(2);
}

// Aplica els canvis pendents de desar sobre les dades confirmades.
// Cada operació és idempotent: si ja està aplicada al servidor, no fa res.
function applyPending(list, pending) {
    let out = { ...list };
    pending.forEach((op) => {
        const racer = out[op.id];
        if (op.type === "addLap") {
            if (racer && racer.laps.length === op.lap) {
                out[op.id] = { ...racer, laps: [...racer.laps, op.time] };
            }
        } else if (op.type === "removeLap") {
            if (racer && racer.laps.length === op.lap + 1) {
                out[op.id] = { ...racer, laps: racer.laps.slice(0, -1) };
            }
        } else if (op.type === "resetLaps") {
            Object.values(out).forEach((r) => {
                if (r.category === op.category && r.laps.length) {
                    out[r.id] = { ...r, laps: [] };
                }
            });
        } else if (op.type === "addRacer") {
            out[op.racer.id] = fromRow(op.racer);
        } else if (op.type === "updateRacer") {
            if (racer) out[op.id] = { ...racer, ...op.fields };
        } else if (op.type === "deleteRacer") {
            delete out[op.id];
        } else if (op.type === "clearRacers") {
            out = {};
        }
    });
    return out;
}

// Posició: més voltes primer i, a igualtat, qui ha passat abans per meta.
// total = hora de l'últim pas per meta.
function withPositions(list) {
    let byCategory = {};
    Object.values(list).forEach((racer) => {
        const total = racer.laps.length ? racer.laps[racer.laps.length - 1] : false;
        const item = { ...racer, total, position: "-" };
        if (!byCategory[racer.category]) byCategory[racer.category] = [];
        byCategory[racer.category].push(item);
    });

    let out = {};
    Object.values(byCategory).forEach((racers) => {
        racers
            .filter((racer) => racer.laps.length)
            .sort((a, b) => b.laps.length - a.laps.length || a.total - b.total)
            .forEach((racer, index) => {
                racer.position = index + 1;
            });
        racers.forEach((racer) => {
            out[racer.id] = racer;
        });
    });
    return out;
}

function enqueue(context, op) {
    return context.dispatch("sync/enqueue", op, { root: true });
}

export const mutations = {
    setRacers(state, data) {
        state.list = data;
    },
    applyRows(state, rows) {
        let racers = { ...state.list };
        (rows || []).forEach((row) => {
            const current = racers[row.id];
            if (current && current.version > (row.version || 0)) return;
            racers[row.id] = fromRow(row);
        });

        state.list = racers;
    },
    removeRacer(state, id) {
        let racers = { ...state.list };
        delete racers[id];

        state.list = racers;
    },
};
export const actions = {
    async loadRacers(context) {
        const { data, error } = await supabase.from("racers").select("*");
        if (error) {
            console.error("Error carregant corredors", error);
            throw error;
        }

        let racers = {};
        data.forEach((row) => {
            racers[row.id] = fromRow(row);
        });

        context.commit("setRacers", racers);
        return true;
    },
    addRacer(context, params) {
        const racer = { id: newId(), ...toFields(params), laps: [] };
        enqueue(context, { type: "addRacer", racer });

        return racer;
    },
    editRacer(context, params) {
        let fields = toFields(params);

        // Les voltes són temps de la cursa de l'antiga categoria: en canviar-la no serveixen
        const current = context.getters.getRacerByID(params.id);
        if (current && current.category !== fields.category) {
            fields.laps = [];
        }

        return enqueue(context, { type: "updateRacer", id: params.id, fields });
    },
    deleteRacer(context, id) {
        return enqueue(context, { type: "deleteRacer", id });
    },
    clearRacers(context) {
        return enqueue(context, { type: "clearRacers" });
    },
    // Marca el pas per meta de la volta params.lap (0 = primera), només si és la següent
    addLap(context, params) {
        const racer = context.getters.getRacerByID(params.id);
        if (!racer || racer.laps.length !== params.lap) return false;

        return enqueue(context, {
            type: "addLap",
            id: params.id,
            lap: params.lap,
            time: params.time,
        });
    },
    // Desfà l'última volta marcada
    removeLap(context, params) {
        const racer = context.getters.getRacerByID(params.id);
        if (!racer || !racer.laps.length) return false;

        return enqueue(context, {
            type: "removeLap",
            id: params.id,
            lap: racer.laps.length - 1,
        });
    },
    resetLaps(context, params) {
        return enqueue(context, { type: "resetLaps", category: params.id });
    },
};
export const getters = {
    all: (state, getters, rootState) =>
        withPositions(applyPending(state.list, rootState.sync.pending)),

    getRacers: (state, getters) => (id) => {
        if (typeof id !== "undefined") {
            let category = {};
            for (var key in getters.all) {
                if (getters.all[key].category == id) {
                    category[key] = { ...getters.all[key] };
                }
            }
            return category;
        }

        return getters.all;
    },

    getRacerByID: (state, getters) => (id) => {
        if (typeof id === "undefined" || !getters.all[id]) {
            return false;
        }

        return getters.all[id];
    },
};
